import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import { BaseButton, BaseInput } from "@wantable/Shared/Components";
import { useAuth } from "@wantable/Shared/Hooks";
import { ErrorIcon, SuccessIcon } from "@wantable/Shared/Icons";

import Divider from "../LoginForm/Divider";
import SocialMediaProviders from "../LoginForm/SocialMediaProviders";
import CloseButton from "../Shared/CloseButton";
import { validationSchema } from "./validator";

import useGoogleAuthentication from "../../hooks/useGoogleAuthentication";
import useFacebookAuthentication from "../../hooks/useFacebookAuthentication";
import { addEnterButtonListener } from "../../helpers/addEnterButtonListener";
import { ModalType}  from "../types";

interface ISignUpFormProps {
  variantLabel: "login" | "signup" | "forgot";
  onClose: () => void;
  onSwitchModal: (modal: ModalType) => void;
  onAfterLogin?: () => void;
}

type SignUpFormStatus = "error" | "success" | null;

const SignUpForm = ({ variantLabel, onClose, onSwitchModal, onAfterLogin }: ISignUpFormProps) => {
  const [signingUp, setSigningUp] = useState(false);
  const [signUpFormStatus, setSignUpFormStatus] = useState<SignUpFormStatus>(null);
  const [formMessage, setFormMessage] = useState("");
  const { loginWithGoogle } = useGoogleAuthentication();
  const { loginWithFacebook } = useFacebookAuthentication();
  const { register } = useAuth();
  const { values, setFieldValue, handleSubmit, errors, touched } = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      password: "",
    },
    validateOnBlur: true,
    validationSchema,
    onSubmit: async ({ firstName, lastName, email, password }) => {
      try {
        setSigningUp(true);
        await register(firstName, lastName, email, password);

        if (onAfterLogin) {
          onAfterLogin();
        }

        onClose();
        setSignUpFormStatus("success");
      } catch (error) {
        // @ts-ignore
        setFormMessage(error.message);
        setSignUpFormStatus("error");
      } finally {
        setSigningUp(false);
      }
    },
  });

  useEffect(() => {
    setFormMessage("");
    setSignUpFormStatus(null);
  }, [values]);

  useEffect(() => {
    return addEnterButtonListener(handleSubmit);
  }, []);

  return (
    <form
      onClick={(event) => event.stopPropagation()}
      onSubmit={(event) => event.preventDefault()}
      className="w-full mx-[13px] md:w-[440px] bg-white rounded-10 py-[30px] px-[15px] md:px-[56px] flex flex-col items-center cursor-auto relative"
    >
      <CloseButton onClick={onClose} />

      <h2 className="text-mobile-heading-3 md:text-desktop-heading-3 font-black font-primary">
        Get started
      </h2>
      <p className="mt-[12px] mb-[10px] text-mobile-body-lg font-secondary">
        Simply enter your details to begin
      </p>
      <div
        className={`w-full flex flex-row items-center justify-center text-mobile-body md:text-desktop-body ${
          signUpFormStatus === "success" ? "text-[#25B900]" : "text-core-red"
        } font-medium font-secondary mb-[15px]`}
      >
        <ErrorIcon hidden={signUpFormStatus !== "error"} className="pr-4" />
        {signUpFormStatus === "success" && <SuccessIcon className="pr-4" />}
        {formMessage}
      </div>

      <fieldset className="w-full flex flex-col items-center gap-[10px]">
        <BaseInput
          type="text"
          placeholder="Enter first name..."
          name="firstName"
          label="First name"
          value={values.firstName}
          onChange={(value) => setFieldValue("firstName", value)}
          onBlur={() => {}}
          error={touched.firstName ? errors.firstName : undefined}
        />

        <BaseInput
          type="text"
          placeholder="Enter last name..."
          name="lastName"
          label="Last name"
          value={values.lastName}
          onChange={(value) => setFieldValue("lastName", value)}
          onBlur={() => {}}
          error={touched.lastName ? errors.lastName : undefined}
        />

        <BaseInput
          type="email"
          placeholder="Enter email..."
          name="email"
          label="Email Address"
          value={values.email}
          onChange={(value) => setFieldValue("email", value)}
          onBlur={() => {}}
          error={touched.email ? errors.email : undefined}
        />

        <BaseInput
          type="password"
          placeholder="Enter password..."
          name="password"
          label="Password"
          value={values.password}
          onChange={(value) => setFieldValue("password", value)}
          onBlur={() => {}}
          error={touched.password ? errors.password : undefined}
        />

        <div className="w-full mt-[27px]">
          <BaseButton
            mode="button"
            onClick={handleSubmit}
            fullWidth
            isSubmitButton
            disabled={signingUp}
            showSpinner={signingUp}
          >
            Get Started
          </BaseButton>
        </div>
      </fieldset>
      {/* @ts-ignore */}
      {window && window.WANTABLE_termsUrl && (
        <p className="text-mobile-body md:text-desktop-body text-grey-600 mt-[12px]">
          {/* @ts-ignore */}
          <a href={window.WANTABLE_termsUrl} target="_blank">
            {/* @ts-ignore */}
            {window.WANTABLE_terms}
          </a>
        </p>
      )}

      <Divider />

      <SocialMediaProviders
        variant={variantLabel}
        onFacebookProvider={loginWithFacebook}
        onGoogleProvider={loginWithGoogle}
      />

      <div className="w-full flex items-center justify-center mt-30">
        <BaseButton mode="button" variant="plainUnderline" onClick={() => onSwitchModal("login")}>
          Already have an account?
        </BaseButton>
      </div>
    </form>
  );
};

export default SignUpForm;
